import { useEffect, useState } from 'react'
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth'
import { auth } from '../lib/firebase'

export type IdentityState =
  | { status: 'signing-in' }
  | { status: 'ready' }
  /** Anonymous sign-in is not enabled for the project, or was refused. */
  | { status: 'unavailable'; reason: string }

/**
 * Get the public view a scoped identity.
 *
 * Derived timing sits behind `signedIn()` in the rules — as a cost control
 * rather than a privacy one, and the rules say so: a leaked link should not
 * become a read key for the entire timing database. The comment there already
 * anticipated share-link visitors reading timing "once they hold a scoped
 * identity", and this is that identity.
 *
 * It is deliberately *not* the same as making timing world-readable. An
 * anonymous account still has a uid, so a read is attributable and can be rate
 * limited or gated behind App Check later, and every membership check keeps
 * failing: an anonymous visitor is a member of nothing, so `declarations` — the
 * one genuinely private thing under a stream session — stays out of reach
 * without a single rule changing.
 *
 * If someone is already signed in as themselves, that identity is used as-is
 * rather than replaced. Signing a member out to look at a share link would be a
 * rude surprise.
 */
export function useAnonymousIdentity(): IdentityState {
  const [state, setState] = useState<IdentityState>({ status: 'signing-in' })

  useEffect(() => {
    let cancelled = false

    const stop = onAuthStateChanged(auth, (user) => {
      if (cancelled) return
      if (user) {
        setState({ status: 'ready' })
        return
      }
      signInAnonymously(auth).catch((e: unknown) => {
        if (cancelled) return
        // Said plainly rather than left as an empty screen. The likeliest cause
        // is that anonymous sign-in has not been enabled for the project, which
        // is a console setting and not something the page can fix.
        setState({
          status: 'unavailable',
          reason: e instanceof Error ? e.message : String(e),
        })
      })
    })

    return () => {
      cancelled = true
      stop()
    }
  }, [])

  return state
}
